import React, { useState, useEffect, useMemo, useCallback } from "react";
import { LiaEditSolid } from "react-icons/lia";
import {
  IoPersonOutline,
  IoLocationOutline,
  IoPhonePortraitOutline,
} from "react-icons/io5";
import { SlCalender } from "react-icons/sl";
import { CiMail } from "react-icons/ci";
import { RxExit } from "react-icons/rx";
import { useNavigate } from "react-router-dom";
import { MdOutlinePhotoCamera } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../redux/actions";
import { toast } from "react-toastify";
import {
  uploadProfileImage,
  getProfileImage,
  deleteProfileImage,
} from "../../redux/actions/imgAction";
import { FaCheckCircle, FaExclamationCircle, FaTrash } from "react-icons/fa";
import { sendEmailVerification } from "../../redux/actions/mailOnayAction";
import { clearImageData } from "../../redux/slices/imgSlice";
import SocialMedia from "./SocialMedia";
const Profil = ({ setIsOpen, setModalType, user, userId }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, imageData } = useSelector((state) => state.img);
  const [emailVerified, setEmailVerified] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const mailOnay = useSelector((state) => state.mailOnay);

  useEffect(() => {
    if (userId) {
      dispatch(getProfileImage(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    const checkEmailVerification = () => {
      if (user?.email) {
        setEmailVerified(user?.isEmailVerified || false);
      } else {
        setEmailVerified(false);
      }
    };

    checkEmailVerification();
  }, [user?.email, user?.isEmailVerified]);

  useEffect(() => {
    if (mailOnay?.loading !== undefined) {
      setIsSendingVerification(mailOnay.loading);
    }
  }, [mailOnay?.loading]);

  const handleLogout = useCallback(async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate("/giris_yap");
    } catch (error) {
      toast.error(error || "Çıkış işlemi başarısız!");
    }
  }, [dispatch, navigate]);

  const handleImageUpload = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Dosya boyutu 5MB'dan küçük olmalıdır!");
        return;
      }

      const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Sadece JPEG, PNG ve JPG formatları desteklenmektedir!");
        return;
      }

      if (!userId) {
        toast.error("Kullanıcı ID'si bulunamadı! Lütfen tekrar giriş yapın.");
        return;
      }

      setSelectedFile(file);
      dispatch(uploadProfileImage({ file, userId }));
    },
    [dispatch, userId]
  );

  const handleSendVerificationEmail = useCallback(() => {
    if (!user?.email) return;
    const redirectUrl = `${window.location.origin}/hesabim/${user?.userName}`;
    dispatch(sendEmailVerification(user?.email, redirectUrl));
    toast.info("Mailinizi kontrol edin. Onay bağlantısı gönderildi.");
  }, [dispatch, user?.email, user?.userName]);

  const handleDeleteImage = useCallback(async () => {
    const currentImage = imageData[userId]?.data?.[0];

    if (currentImage?._id) {
      try {
        await dispatch(deleteProfileImage(currentImage._id)).unwrap();
      } catch (error) {
      } finally {
        dispatch(clearImageData(userId));
      }
    } else {
      toast.error("Silinecek profil fotoğrafı bulunamadı!");
    }
  }, [dispatch, imageData, userId]);

  const handleEditClick = useCallback(() => {
    setModalType("Kişisel Bilgiler");
    setIsOpen(true);
  }, [setModalType, setIsOpen]);

  const handleContactClick = useCallback(() => {
    setModalType("İletişim Bilgileri");
    setIsOpen(true);
  }, [setModalType, setIsOpen]);

  const hasProfileImage = useMemo(
    () => imageData[userId]?.data?.length > 0,
    [imageData, userId]
  );

  const socialMediaInfo = useMemo(() => {
    if (!user)
      return {
        linkedin: "--",
        instagram: "--",
        github: "--",
        behance: "--",
      };

    return {
      linkedin:
        (
          user.social_medias?.find((s) => s.startsWith("linkedin")) || ""
        ).replace("linkedin:", "") || "--",
      instagram:
        (
          user.social_medias?.find((s) => s.startsWith("instagram")) || ""
        ).replace("instagram:", "") || "--",
      github:
        (user.social_medias?.find((s) => s.startsWith("github")) || "").replace(
          "github:",
          ""
        ) || "--",
      behance:
        (
          user.social_medias?.find((s) => s.startsWith("behance")) || ""
        ).replace("behance:", "") || "--",
    };
  }, [user?.social_medias]);

  const personalInfo = useMemo(() => {
    if (!user)
      return {
        gender: "--",
        address: "--",
        birthDate: "--",
      };

    return {
      gender:
        user.gender === "FEMALE"
          ? "Kadın"
          : user.gender === "MALE"
          ? "Erkek"
          : user.gender === "OTHER"
          ? "Diğer"
          : "--",
      address: user.address || "--",
      birthDate: user.birthDate
        ? new Date(user.birthDate).toLocaleDateString("tr-TR")
        : "--",
    };
  }, [user?.gender, user?.address, user?.birthDate]);

  return (
    <div className="detay-container">
      <div className="profil1 flex flex-col gap-4 relative items-center">
        <div className="relative w-fit group">
          <img
            src={
              imageData[userId]?.data?.length > 0
                ? `data:image/jpeg;base64,${imageData[userId].data[0].content}`
                : "/defaultPerson.svg"
            }
            alt="person"
            className="size-[100px] sm:size-[152px] rounded-full border-2 border-gray-300 object-cover transition-all duration-300 group-hover:brightness-90"
          />

          {/* Upload Button */}
          <div className="absolute bottom-0 right-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
            <label htmlFor="profileImageInput" className="block cursor-pointer">
              <div className="bg-white hover:bg-gray-100 rounded-full p-2 shadow-md border border-gray-300">
                {loading ? (
                  <div className="size-5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <MdOutlinePhotoCamera className="size-5 text-gray-600 hover:text-gray-800" />
                )}
              </div>
            </label>
          </div>

          {/* Delete Button */}
          {hasProfileImage && (
            <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
              <button
                type="button"
                onClick={handleDeleteImage}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-md"
                title="Fotoğrafı Sil"
                disabled={loading}
              >
                <FaTrash className="size-4" />
              </button>
            </div>
          )}
        </div>

        {/* File Input - Outside of the image container */}
        <input
          id="profileImageInput"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
          disabled={loading}
        />

        {error && (
          <p className="text-red-500 text-sm bg-red-50 px-4 py-2 rounded-full">
            {error}
          </p>
        )}
        <p className="text-dark text-2xl font-medium">{user?.name}</p>
        <p className="text-sm text-grayy whitespace-nowrap">
          {user?.position?.name}
        </p>
      </div>
      <div className="profil2 flex flex-col gap-4">
        <p className="flex justify-between mt-4">
          <span className="text-dark text-lg tracking-wide font-medium">
            Kişisel Bilgiler
          </span>
          <span className="text-grayy" onClick={handleEditClick}>
            <LiaEditSolid className="size-[24px]" />
          </span>
        </p>
        <ul className="flex flex-col">
          <li className="text-grayy flex items-center gap-4 mb-2">
            <IoPersonOutline className="size-[20px]" />
            <span className="text-sm">{personalInfo.gender}</span>
          </li>
          <li className="text-grayy flex items-center gap-4 my-2">
            <IoLocationOutline className="size-[20px]" />
            <span className="text-sm flex gap-4">{personalInfo.address}</span>
          </li>
          <li className="text-grayy flex items-center gap-4 my-2">
            <SlCalender className="size-[20px]" />
            <span className="text-sm">{personalInfo.birthDate}</span>
          </li>
        </ul>
      </div>
      <div className="profil3 flex flex-col gap-4">
        <p className="flex justify-between mt-4">
          <span className="text-dark text-lg tracking-wide font-medium">
            İletişim Bilgileri
          </span>
          <span className="text-grayy" onClick={handleContactClick}>
            <LiaEditSolid className="size-[24px]" />
          </span>
        </p>
        <ul>
          <li className="text-grayy flex items-center gap-4 mb-2">
            <CiMail className="size-[20px]" />
            <span className="text-sm break-all flex items-center gap-2">
              {user?.email || "--"}
              {user?.email &&
                (user?.isEmailVerified ? (
                  <FaCheckCircle
                    className="text-green-500 size-4"
                    title="E-posta onaylandı"
                  />
                ) : (
                  <div
                    className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={handleSendVerificationEmail}
                    title="Onay e-postası gönder"
                  >
                    <FaExclamationCircle
                      className="text-yellow-500 size-4"
                      title="E-posta onaylanmadı"
                    />
                    <span className="text-xs text-yellow-500">
                      {isSendingVerification
                        ? "Gönderiliyor..."
                        : "Onay için tıklayınız!"}
                    </span>
                  </div>
                ))}
            </span>
          </li>
          <li className="text-grayy flex items-center gap-4 my-2">
            <IoPhonePortraitOutline className="size-[20px]" />
            <span className="text-sm flex gap-4">
              {user?.telephone || "--"}
            </span>
          </li>
        </ul>
      </div>
      <SocialMedia
        socialMediaInfo={socialMediaInfo}
        onEditClick={() => {
          setModalType("Sosyal Medya Hesapları");
          setIsOpen(true);
        }}
        showEdit={true}
      />
      <div className="grid place-items-center py-4">
        <button
          onClick={handleLogout}
          className="btn-transparent flex gap-4 items-center"
        >
          <span>Çıkış Yap</span>
          <RxExit className="size-4 text-errorRed font-semibold" />
        </button>
      </div>
    </div>
  );
};

export default React.memo(Profil);
